import React, { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import {
  TopbarButtonProps,
  TopbarButton,
  TopbarDropdownProps,
  TopbarDropdown,
  DropdownTheme,
} from '@teko/teko-ecommerce';
import type { IconBaseProps } from '@teko/ds-icon';

const BusinessCustomerOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.BusinessCustomerOutlined),
);
const HeadphoneOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.HeadphoneOutlined),
);
const ShowroomOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.ShowroomOutlined),
);
const TvNewOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.TvNewOutlined),
);
const PromoOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.PromoOutlined),
);
const BuildPcOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.BuildPcOutlined),
);
const MailOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.MailOutlined),
);
const FacebookFilled = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.FacebookFilled),
);
const ShopOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.ShopOutlined),
);
const PhoneOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.PhoneOutlined),
);

const BriefCaseOutlined = dynamic(() =>
  import('@teko/ds-icon').then((mod) => mod.BriefCaseOutlined),
);

interface IconData {
  iconName: string;
  color: string;
  size: number;
}

type TopbarButtonPropsWithoutIcon = Omit<TopbarButtonProps, 'icon'>;
type TopbarDropdownPropsWithoutIcon = Omit<TopbarDropdownProps, 'icon'>;

export type ITopBarButtonConfig = TopbarButtonPropsWithoutIcon &
  TopbarDropdownPropsWithoutIcon & {
    key?: number;
    icon?: IconData;
    menu?: (TopbarButtonPropsWithoutIcon & {
      key?: number;
      icon?: IconData;
    })[];
  };

/* map from iconName to component icon */
const iconMap = new Map<string, React.ComponentType<IconBaseProps>>([
  ['BusinessCustomerOutlined', BusinessCustomerOutlined],
  ['HeadphoneOutlined', HeadphoneOutlined],
  ['TvNewOutlined', TvNewOutlined],
  ['ShowroomOutlined', ShowroomOutlined],
  ['BuildPcOutlined', BuildPcOutlined],
  ['PromoOutlined', PromoOutlined],
  ['MailOutlined', MailOutlined],
  ['FacebookFilled', FacebookFilled],
  ['ShopOutlined', ShopOutlined],
  ['PhoneOutlined', PhoneOutlined],
  ['BriefCaseOutlined', BriefCaseOutlined],
]);

const renderTopBarIcon = (iconData: IconData) => {
  const { iconName, ...rest } = iconData;
  const Comp = iconMap.get(iconName);
  return Comp ? <Comp {...rest} /> : null;
};

/* render top bar button by its config */
export const renderTopBarButton = (
  topBarButtonConfig: ITopBarButtonConfig,
): ReactNode => {
  const { children, key, icon, menu, ...rest } = topBarButtonConfig;

  const props = {
    ...(!!icon ? { icon: renderTopBarIcon(icon) } : {}),
    ...rest,
  };

  if (menu?.length) {
    return (
      <TopbarDropdown
        key={key}
        text={children}
        {...props}
        theme={DropdownTheme.MENU}
        options={menu.map(({ children, href, icon }) => ({
          text: typeof children === 'string' ? children : '',
          value: href || '#',
          icon: icon ? renderTopBarIcon(icon) ?? undefined : undefined,
        }))}
        selectValue={(href) => {
          if (typeof href === 'string') {
            window.location.href = href;
          }
        }}
      />
    );
  }

  return (
    <TopbarButton {...props} key={key}>
      {children}
    </TopbarButton>
  );
};
